import React from "react";
import { Chart, ChartArea, ChartLegend, ChartSeries, ChartSeriesItem, ChartTooltip, ChartXAxis, ChartXAxisItem, ChartYAxis, ChartYAxisItem } from "@progress/kendo-react-charts";

interface EfficiencyCurveProps {
    chart1Data?: any;
}

const EfficiencyCurve: React.FC<EfficiencyCurveProps> = ({ chart1Data }) => {
    return (
        <div>
            {chart1Data && (
                <Chart>
                    <ChartArea background="transparent" />

                    {/* Legend */}
                    <ChartLegend
                        position="bottom"
                        labels={{ font: "16px Mulish", color: "#fff" }}
                    />

                    {/* Series */}
                    <ChartSeries>
                        <ChartSeriesItem
                            name="Head"
                            type="scatterLine"
                            data={chart1Data?.series?.[0]?.data}
                            xField="capacity"
                            yField="head"
                            color="#0000FF"
                            yAxis="Head"
                            style="smooth"
                            markers={{ visible: false }}
                            width={4.0}
                            tooltip={{ visible: true, format: "{0} x {1}", color: "#FFF" }}
                        />
                        <ChartSeriesItem
                            name="Power"
                            type="scatterLine"
                            data={chart1Data?.series?.[1]?.data}
                            xField="capacity"
                            yField="power"
                            color="#FF0000"
                            yAxis="Power"
                            style="smooth"
                            markers={{ visible: false }}
                            width={4.0}
                            tooltip={{ visible: true, format: "{0} x {1}", color: "#FFF" }}
                        />
                        <ChartSeriesItem
                            name="Efficiency"
                            type="scatterLine"
                            data={chart1Data?.series?.[2]?.data}
                            xField="capacity"
                            yField="efficiency"
                            color="#006633"
                            yAxis="Efficiency"
                            style="smooth"
                            markers={{ visible: false }}
                            width={4.0}
                            tooltip={{ visible: true, format: "{0} x {1}", color: "#FFF" }}
                        />
                        <ChartSeriesItem
                            name=""
                            type="scatterLine"
                            data={chart1Data?.series?.[3]?.data}
                            xField="capacity"
                            yField="head"
                            color="#737373"
                            yAxis="Head"
                            dashType="longDash"
                            markers={{
                                visible: false,
                                type: "square",
                                background: "black",
                                size: 10,
                            }}
                            width={2.0}
                            labels={{
                                visible: true,
                                background: "#bfbfbf",
                                border: {
                                    width: 1,
                                    color: "black",
                                },
                                font: "11px calibri",
                            }}
                            tooltip={{
                                visible: true,
                                format: "{0} x {1}",
                                color: "#FFF",
                            }}
                            visibleInLegend={false}
                        />
                        <ChartSeriesItem
                            name="BEPFlow"
                            type="scatterLine"
                            data={chart1Data?.series?.[4]?.data}
                            xField="capacity"
                            yField="head"
                            color="#006633"
                            yAxis="Head"
                            dashType="longDash"
                            markers={{
                                visible: false,
                                type: "square",
                                background: "black",
                                size: 10,
                            }}
                            width={2.0}
                            labels={{
                                visible: true,
                                background: "#bfbfbf",
                                border: {
                                    width: 1,
                                    color: "black",
                                },
                                font: "11px calibri",
                            }}
                            tooltip={{
                                visible: true,
                                format: "{0} x {1}",
                                color: "#FFF",
                            }}
                            visibleInLegend={false}
                        />
                        <ChartSeriesItem
                            name="MaxOperatingFlow"
                            type="scatterLine"
                            data={chart1Data?.series?.[5]?.data}
                            xField="capacity"
                            yField="head"
                            color="#737373"
                            yAxis="Head"
                            dashType="longDash"
                            markers={{
                                visible: false,
                                type: "square",
                                background: "black",
                                size: 10,
                            }}
                            width={2.0}
                            labels={{
                                visible: true,
                                background: "#bfbfbf",
                                border: {
                                    width: 1,
                                    color: "black",
                                },
                                font: "11px calibri",
                            }}
                            tooltip={{
                                visible: true,
                                format: "{0} x {1}",
                                color: "#FFF",
                            }}
                            visibleInLegend={false}
                        />
                        <ChartSeriesItem
                            name="Target"
                            type="scatter"
                            data={chart1Data?.series?.[6]?.data}
                            xField="capacity"
                            yField="head"
                            color="#4d4d4d"
                            yAxis="Head"
                            markers={{
                                visible: true,
                                type: "square",
                                background: "black",
                                size: 10,
                            }}
                            width={2.0}
                            labels={{
                                visible: true,
                                background: "#bfbfbf",
                                border: {
                                    width: 1,
                                    color: "black",
                                },
                                font: "11px calibri",
                            }}
                            tooltip={{
                                visible: true,
                            }}
                            visibleInLegend={false}
                        />
                        <ChartSeriesItem
                            name="TargetH"
                            type="scatterLine"
                            data={chart1Data?.series?.[7]?.data}
                            xField="capacity"
                            yField="head"
                            color="#bfbfbf"
                            yAxis="Head"
                            markers={{ visible: true }}
                            width={2.0}
                            dashType="dot"
                            tooltip={{
                                visible: false,
                            }}
                            visibleInLegend={false}
                        />
                        <ChartSeriesItem
                            name="TargetV"
                            type="scatterLine"
                            data={chart1Data?.series?.[8]?.data}
                            xField="capacity"
                            yField="head"
                            color="#bfbfbf"
                            yAxis="Head"
                            markers={{ visible: true }}
                            width={2.0}
                            dashType="dot"
                            tooltip={{
                                visible: false,
                            }}
                            visibleInLegend={false}
                        />
                    </ChartSeries>

                    {/* X Axis */}
                    <ChartXAxis>
                        <ChartXAxisItem
                            name="Capacity"
                            title={{
                                text: `${chart1Data?.xAxis?.title?.text}`,
                                font: "16px Mulish",
                                color: "#fff",
                            }}
                            color="#fff"
                            axisCrossingValue={chart1Data?.xAxis?.axisCrossingValue}
                            majorGridLines={{ width: 0.8, color: "#A0A0A0" }}
                            minorGridLines={{ width: 0.3, color: "#A0A0A0", visible: true }}
                            crosshair={{
                                visible: true,
                                width: 1,
                                color: "#005F7C",
                                tooltip: {
                                    visible: true,
                                    format: "#.##",
                                },
                            }}
                            min={0}
                            max={chart1Data?.xAxis?.xAxisCrossingValues?.[1]}
                        />
                    </ChartXAxis>
                    {/* Y Axis */}
                    <ChartYAxis>
                        <ChartYAxisItem
                            name="Head"
                            title={{
                                text: `${chart1Data?.yAxis?.[0]?.title?.text}`,
                                font: "16px Mulish",
                                color: "#0000FF",
                            }}
                            min={0}
                            // max={chart1Data?.yAxis[0]?.majorUnit + 1500}
                            color="#0000FF"
                            majorGridLines={{ width: 0.8, color: "#A0A0A0" }}
                            minorGridLines={{ width: 0.3, color: "#A0A0A0", visible: true }}
                            crosshair={{ visible: true, width: 1, color: "#005F7C" }}
                            majorUnit={chart1Data?.yAxis?.[0]?.majorUnit}
                        />
                        <ChartYAxisItem
                            name="Power"
                            title={{
                                text: `${chart1Data?.yAxis?.[1]?.title?.text}`,
                                font: "16px Mulish",
                                color: "#FF0000",
                            }}
                            min={0}
                            max={chart1Data?.yAxis?.[1]?.max}
                            color="#FF0000"
                            crosshair={{ visible: true, width: 1, color: "#005F7C" }}
                            majorUnit={chart1Data?.yAxis?.[1]?.majorUnit}
                        />
                        <ChartYAxisItem
                            name="Efficiency"
                            title={{
                                text: `${chart1Data?.yAxis?.[2]?.title?.text}`,
                                font: "16px Mulish",
                                color: "#006633",
                            }}
                            min={0}
                            majorUnit={chart1Data?.yAxis?.[2]?.majorUnit}
                            // max={Math.ceil(data?.maxScales?.efficiency / 10) * 10}
                            color="#006633"
                            crosshair={{ visible: true, width: 1, color: "#005F7C" }}
                        />
                    </ChartYAxis>

                    {/* Tooltip */}
                    <ChartTooltip
                        visible={true}
                        color="#FFF"
                        opacity={1.0}
                        format="{0} x {1}"
                    />
                </Chart>
            )}
        </div>
    );
};

export default EfficiencyCurve;
